import React, { useState, useEffect, useMemo } from 'react';
import { Typography, Spin, Empty, Tag, Alert, Input, message } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { EntityImage } from '../Entities/EntityImage';
import { useApi } from '../../hooks/use_api';
import { useNodeInfo } from '../../hooks/use_node_info';
import '../Home/Homepage.css';

const { Title, Text, Paragraph } = Typography;

/**
 * Shared by peers (/shared) — the directory of what other federation servers
 * share with this node. Each peer this node approved is a section listing the
 * entities its admins marked shared (backend routers/entities.py
 * shared_by_peers, which asks every such peer's /federation/shared). This is
 * how a member finds a remote entity now that there is no search across
 * peers: a row resolves the entity's global URI to a local shadow (POST
 * /api/entities/resolve-uri) and opens it, the path a federated search result
 * used to take. The filter box narrows client-side: the listing is what
 * admins chose to share, not a node's whole graph, so a page of it is small.
 */

function matches(item, needle) {
    if (!needle) return true;
    const q = needle.toLowerCase();
    return (item.name || '').toLowerCase().includes(q) || (item.description || '').toLowerCase().includes(q);
}

export default function SharedByPeers() {
    const api = useApi();
    const navigate = useNavigate();
    const { ready, federationEnabled, featureEntityTypeIdentifiers } = useNodeInfo();
    // null until the fetch settles, so the page can tell loading from empty.
    const [peers, setPeers] = useState(null);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState('');
    // The uri being resolved, so its row can show that it is opening.
    const [opening, setOpening] = useState(null);

    useEffect(() => {
        // Wait for /info: the route 404s on a node without federation, and
        // there is nothing to list there anyway.
        if (!ready) return undefined;
        if (!federationEnabled) { setPeers([]); return undefined; }
        let alive = true;
        api.get('/api/entities/shared-by-peers')
            .then(data => { if (alive) { setPeers(data.peers || []); setError(null); } })
            .catch(err => { if (alive) { setPeers([]); setError(err.message); } });
        return () => { alive = false; };
    }, [api, ready, federationEnabled]);

    const open = async (item) => {
        if (!item.uri) { navigate(`/entity/${item.id}`); return; }
        setOpening(item.uri);
        try {
            const e = await api.post('/api/entities/resolve-uri', { uri: item.uri });
            navigate(`/entity/${e.id}`);
        } catch {
            message.error(`Couldn't open ${item.name} — its server may be unreachable`);
        } finally {
            setOpening(null);
        }
    };

    const needle = filter.trim();
    const sections = useMemo(
        () => (peers || []).map(p => ({ ...p, shown: (p.items || []).filter(i => matches(i, needle)) })),
        [peers, needle],
    );
    const nothingShared = peers !== null && peers.length > 0 && peers.every(p => p.ok && (p.items || []).length === 0);

    let body;
    if (!ready || peers === null) {
        body = <div style={{ textAlign: 'center', padding: 48 }}><Spin /></div>;
    } else if (!federationEnabled) {
        body = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Federation is off on this node" />;
    } else if (error) {
        body = <Alert type="error" showIcon title="Couldn't load what peers share" description={error} />;
    } else if (peers.length === 0) {
        body = <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No approved peers yet" />;
    } else {
        body = (
            <>
                <Input
                    allowClear
                    prefix={<SearchOutlined style={{ color: 'var(--text-secondary)' }} />}
                    placeholder="Filter by name or description"
                    value={filter}
                    onChange={e => setFilter(e.target.value)}
                    style={{ marginBottom: 24, maxWidth: 480 }}
                />
                {nothingShared && (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Peers aren't sharing anything yet" style={{ marginBottom: 24 }} />
                )}
                {sections.map(peer => (
                    <section key={peer.server_id} style={{ marginBottom: 32 }}>
                        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
                            <Tag color="geekblue" style={{ margin: 0 }}>@{peer.server_id}</Tag>
                            {peer.ok && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    {peer.total} shared
                                </Text>
                            )}
                        </div>
                        {!peer.ok ? (
                            <Alert
                                type="warning"
                                showIcon
                                title={`Couldn't reach ${peer.server_id}`}
                                description="It may be down, or it hasn't approved this node yet."
                            />
                        ) : peer.items.length === 0 ? (
                            <Text type="secondary">Nothing shared yet.</Text>
                        ) : peer.shown.length === 0 ? (
                            <Text type="secondary">No match here.</Text>
                        ) : (
                            <div role="list">
                                {peer.shown.map(item => (
                                    <div
                                        key={item.uri || item.id}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`Open ${item.name}`}
                                        onClick={() => open(item)}
                                        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(item); } }}
                                        style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 6px', borderRadius: 8, cursor: 'pointer' }}
                                        onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-secondary)'; }}
                                        onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
                                    >
                                        <EntityImage entity={item} size={36} />
                                        <div style={{ minWidth: 0, flex: 1 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                                <Text strong>{item.name}</Text>
                                                {featureEntityTypeIdentifiers && (
                                                    <Tag style={{ fontSize: 10, margin: 0, textTransform: 'capitalize' }}>{item.entity_type}</Tag>
                                                )}
                                                {opening === item.uri && <Spin size="small" />}
                                            </div>
                                            {item.description && (
                                                <Text
                                                    type="secondary"
                                                    style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12 }}
                                                >
                                                    {item.description}
                                                </Text>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                        {peer.ok && peer.total > peer.items.length && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                Showing the first {peer.items.length} of {peer.total}.
                            </Text>
                        )}
                    </section>
                ))}
            </>
        );
    }

    return (
        <div className="browse-page" style={{ maxWidth: 900, margin: '0 auto' }}>
            <Title level={3} style={{ marginBottom: 4 }}>Shared by peers</Title>
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
                What other servers in this federation share with this node. Open one to see it here;
                asking to join goes to its home server.
            </Paragraph>
            {body}
        </div>
    );
}
